import { useState } from 'react';
import { getStore } from '../../data/store.ts';
import type { SupabaseSource } from '../../data/supabaseSource.ts';
import { Icon } from '../../components/Icon.tsx';

interface Progress {
  total: number;
  done: number;
  failed: number;
  current?: string;
  finished: boolean;
}

/**
 * The rarely used Sync button. Emails are processed as they arrive, so this only retries
 * emails that failed (e.g. AI was down) and refreshes everything, with a real progress bar
 * (one step per email).
 */
export function SyncButton() {
  const store = getStore();
  const [progress, setProgress] = useState<Progress | null>(null);

  const run = async () => {
    if (progress && !progress.finished) return;
    if (store.source.kind !== 'supabase') {
      setProgress({ total: 0, done: 0, failed: 0, finished: true });
      store.toast('Sync works once the inbox is connected. Showing sample data for now.');
      setTimeout(() => setProgress(null), 2500);
      return;
    }
    const source = store.source as SupabaseSource;
    const toRetry = store.state.emails.filter((e) => e.status === 'failed' || e.status === 'pending');
    const p: Progress = { total: toRetry.length, done: 0, failed: 0, finished: false };
    setProgress({ ...p });
    for (const e of toRetry) {
      setProgress({ ...p, current: e.subject });
      try {
        const r = await source.invoke<{ status: string }>('ingest-email', { retryEmailId: e.id });
        if (r.status === 'failed') p.failed += 1;
      } catch {
        p.failed += 1;
      }
      p.done += 1;
      setProgress({ ...p });
    }
    await store.init();
    setProgress({ ...p, finished: true, current: undefined });
    setTimeout(() => setProgress(null), 4000);
  };

  const pct = progress && progress.total ? Math.round((progress.done / progress.total) * 100) : progress?.finished ? 100 : 0;

  return (
    <div className="stack" style={{ gap: 8, alignItems: 'flex-end' }}>
      <button type="button" className="icon-btn" aria-label="Sync now" onClick={run} disabled={!!progress && !progress.finished}>
        <Icon name="refresh" size={20} />
      </button>
      {progress && progress.total > 0 && (
        <div className="card stack" style={{ position: 'absolute', right: 20, top: 'calc(80px + var(--safe-top))', width: 'min(320px, calc(100% - 40px))', zIndex: 10, gap: 8 }} role="status">
          <span className="small" style={{ fontWeight: 600 }}>
            {progress.finished ? `Sync done · ${progress.total - progress.failed} of ${progress.total} read` : `Processing ${progress.done + 1} of ${progress.total}…`}
          </span>
          <div className="progress gold" aria-hidden="true">
            <div style={{ width: `${pct}%` }} />
          </div>
          {progress.current && <span className="tiny muted">{progress.current}</span>}
          {progress.finished && progress.failed > 0 && <span className="tiny" style={{ color: 'var(--orange-text)' }}>{progress.failed} still need a look.</span>}
        </div>
      )}
    </div>
  );
}
